"use client";
// Reusable Contact-us inquiry form: pick a channel (Email/Call/WhatsApp), leave
// your detail (+ optional email receipt), company, and a message. Submits to the
// dashboard which stores it, notifies the team, and emails the visitor. Used
// inline (contact section) and inside ContactModal. Bilingual via next-intl.
import { useEffect, useRef, useState, useTransition } from "react";
import Script from "next/script";
import { useLocale } from "next-intl";
import { Mail, Phone, MessageCircle, Check, Loader2 } from "lucide-react";
import { submitInquiry, type InquiryChannel, type InquiryType } from "@/actions/submit-inquiry";
import { WHATSAPP_NUMBER } from "@/lib/website/contact";

const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? "";

type Props = {
  courseSlug?: string | null;
  courseTitle?: string | null;
  defaultType?: InquiryType;
  onDone?: () => void;
};

const TYPE_OPTIONS: { value: InquiryType; en: string; ar: string }[] = [
  { value: "enrollment", en: "Course enrollment", ar: "التسجيل في دورة" },
  { value: "pricing", en: "Pricing", ar: "الأسعار" },
  { value: "corporate", en: "Corporate / group training", ar: "تدريب مؤسسي / جماعي" },
  { value: "support", en: "Technical / account help", ar: "مساعدة تقنية / حساب" },
  { value: "general", en: "General question", ar: "سؤال عام" },
  { value: "other", en: "Other", ar: "أخرى" },
];

export function ContactForm({ courseSlug = null, courseTitle = null, defaultType, onDone }: Props) {
  const locale = useLocale();
  const tt = (en: string, ar: string) => (locale === "ar" ? ar : en);
  const [pending, start] = useTransition();
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [type, setType] = useState<InquiryType>(defaultType ?? (courseSlug ? "enrollment" : "general"));
  const [channel, setChannel] = useState<InquiryChannel>("whatsapp");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");

  const [token, setToken] = useState<string | null>(null);
  const tsRef = useRef<HTMLDivElement | null>(null);
  const widgetId = useRef<string | null>(null);

  function renderTurnstile() {
    if (!TURNSTILE_SITE_KEY || !tsRef.current || !window.turnstile || widgetId.current) return;
    widgetId.current = window.turnstile.render(tsRef.current, { sitekey: TURNSTILE_SITE_KEY, callback: (tok) => setToken(tok) });
  }
  function resetTurnstile() {
    if (widgetId.current && window.turnstile) { window.turnstile.reset(widgetId.current); setToken(null); }
  }
  useEffect(() => {
    if (TURNSTILE_SITE_KEY && typeof window !== "undefined" && window.turnstile) renderTurnstile();
  }, []);

  const channels: { value: InquiryChannel; en: string; ar: string; Icon: typeof Mail }[] = [
    { value: "whatsapp", en: "WhatsApp", ar: "واتساب", Icon: MessageCircle },
    { value: "call", en: "Phone call", ar: "مكالمة", Icon: Phone },
    { value: "email", en: "Email", ar: "بريد إلكتروني", Icon: Mail },
  ];
  const captchaPending = !!TURNSTILE_SITE_KEY && !token;
  const channelLabel = channels.find((c) => c.value === channel)!;
  // Number comes from lib/website/contact so the form, the floating button and
  // the footer can never show three different numbers. It used to be fetched
  // from dashboard settings through a helper that double-unwrapped the API
  // envelope and therefore always resolved to null — the button never rendered.
  const waHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message || tt("Hi FortMind, I have a question.", "مرحباً فورت‑مايند، لديّ سؤال."))}`;

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function translateError(err: string): string {
    if (locale !== "ar") return err;
    const lower = err.toLowerCase();
    if (lower.includes("invalid form data")) return "بيانات النموذج غير صالحة.";
    if (lower.includes("valid email")) return "يرجى إدخال بريد إلكتروني صحيح.";
    if (lower.includes("valid phone")) return "يرجى إدخال رقم هاتف صحيح.";
    if (lower.includes("receipt looks invalid")) return "بريد إيصال التأكيد غير صحيح.";
    if (lower.includes("too many messages") || lower.includes("rate limit")) return "عدد كبير من الرسائل. يرجى المحاولة بعد بضع دقائق.";
    if (lower.includes("verification challenge") || lower.includes("turnstile") || lower.includes("captcha")) return "يرجى إكمال اختبار التحقق.";
    if (lower.includes("network error") || lower.includes("fetch failed") || lower.includes("failed to fetch")) return "تعذّر الاتصال بالخادم. يرجى التحقق من اتصالك بالإنترنت أو المحاولة لاحقاً.";
    if (lower.includes("something went wrong")) return "حدث خطأ ما. يرجى المحاولة مرة أخرى.";
    return err;
  }

  function submit() {
    setError(null);

    const cleanName = name.trim();
    if (cleanName.length < 2) {
      setError(tt("Please enter your name (at least 2 characters).", "يرجى إدخال اسمك (حرفين على الأقل)."));
      return;
    }

    const cleanEmail = email.trim();
    const cleanPhone = phone.trim();

    if (channel === "email") {
      if (!cleanEmail || !EMAIL_RE.test(cleanEmail)) {
        setError(tt("Please enter a valid email address.", "يرجى إدخال بريد إلكتروني صحيح."));
        return;
      }
    } else {
      if (!cleanPhone || cleanPhone.replace(/\D/g, "").length < 6) {
        setError(tt("Please enter a valid phone number.", "يرجى إدخال رقم هاتف صحيح."));
        return;
      }
      if (cleanEmail && !EMAIL_RE.test(cleanEmail)) {
        setError(tt("The email for your receipt looks invalid.", "بريد إيصال التأكيد غير صحيح."));
        return;
      }
    }

    if (captchaPending) {
      setError(tt("Please complete the verification challenge.", "يرجى إكمال اختبار التحقق."));
      return;
    }

    start(async () => {
      const r = await submitInquiry({
        type,
        name: cleanName,
        channel,
        email: cleanEmail || null,
        phone: cleanPhone || null,
        company: company.trim() || null,
        message: message.trim() || null,
        courseSlug,
        locale: locale === "ar" ? "ar" : "en",
        turnstile_token: token,
      });
      if (!r.success) {
        setError(translateError(r.error));
        resetTurnstile();
        return;
      }
      setDone(true);
    });
  }

  if (done) {
    return (
      <div className="space-y-4 py-4 text-center">
        <div className="mx-auto grid size-12 place-items-center rounded-full bg-(--fm-success)/15 text-(--fm-success)">
          <Check className="size-6" />
        </div>
        <p className="text-(--fm-text-primary)">
          {tt(
            `Thanks, ${name || "there"}! We've got your message and will reach out via ${channelLabel.en} soon.`,
            `شكراً${name ? "، " + name : ""}! وصلتنا رسالتك وسنتواصل معك عبر ${channelLabel.ar} قريباً.`
          )}
        </p>
        {email && <p className="text-sm text-(--fm-text-secondary)">{tt("A confirmation was sent to your email.", "تم إرسال تأكيد إلى بريدك الإلكتروني.")}</p>}
        {channel === "whatsapp" && (
          <a href={waHref} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-lg bg-[#25D366] px-4 py-2 font-semibold text-[#0A1628]">
            <MessageCircle className="size-4" /> {tt("Chat with us on WhatsApp now", "تحدّث معنا على واتساب الآن")}
          </a>
        )}
        {onDone && (
          <button type="button" onClick={onDone} className="block w-full rounded-lg border border-(--fm-border) px-4 py-2 text-sm text-(--fm-text-primary)">
            {tt("Done", "تمّ")}
          </button>
        )}
      </div>
    );
  }

  return (
    <form 
      onSubmit={(e) => { e.preventDefault(); submit(); }}
      toolname="contact_fortmind"
      tooldescription="Send an inquiry or message to the FortMind team regarding cybersecurity training or support."
      className="space-y-4"
    >
      {TURNSTILE_SITE_KEY && (
        <Script src="https://challenges.cloudflare.com/turnstile/v0/api.js" strategy="afterInteractive" onLoad={renderTurnstile} />
      )}
      {courseTitle && (
        <span className="inline-block rounded-full bg-(--fm-primary)/10 px-2 py-0.5 text-xs text-(--fm-primary)">
          {tt("About", "بخصوص")}: {courseTitle}
        </span>
      )}

      <div>
        <label className="mb-1.5 block text-xs font-medium text-(--fm-text-secondary)">{tt("How should we reach you?", "كيف نتواصل معك؟")}</label>
        <div className="grid grid-cols-3 gap-2">
          {channels.map(({ value, en, ar, Icon }) => (
            <button key={value} type="button" onClick={() => setChannel(value)}
              className={`flex flex-col items-center gap-1 rounded-xl border px-2 py-3 text-xs transition ${
                channel === value ? "border-(--fm-primary) bg-(--fm-primary)/10 text-(--fm-primary)" : "border-(--fm-border) text-(--fm-text-secondary) hover:border-(--fm-primary)/40"
              }`}>
              <Icon className="size-5" />
              {tt(en, ar)}
            </button>
          ))}
        </div>
      </div>

      <label className="block space-y-1">
        <span className="text-xs font-medium text-(--fm-text-secondary)">{tt("What is this about?", "ما موضوع الاستفسار؟")}</span>
        <select value={type} onChange={(e) => setType(e.target.value as InquiryType)}
          toolparamdescription="The category or topic of your inquiry"
          className="w-full rounded-lg border border-(--fm-border) bg-(--fm-background) px-3 py-2 text-sm text-(--fm-text-primary)">
          {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{tt(o.en, o.ar)}</option>)}
        </select>
      </label>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="block space-y-1">
          <span className="text-xs font-medium text-(--fm-text-secondary)">{tt("Your name", "اسمك")}</span>
          <input value={name} onChange={(e) => setName(e.target.value)}
            toolparamdescription="Your full name"
            className="w-full rounded-lg border border-(--fm-border) bg-(--fm-background) px-3 py-2 text-sm text-(--fm-text-primary)"
            placeholder={tt("e.g. Ahmed", "مثال: أحمد")} />
        </label>
        <label className="block space-y-1">
          <span className="text-xs font-medium text-(--fm-text-secondary)">{tt("Company (optional)", "الشركة (اختياري)")}</span>
          <input value={company} onChange={(e) => setCompany(e.target.value)}
            toolparamdescription="Your company or organization name (optional)"
            className="w-full rounded-lg border border-(--fm-border) bg-(--fm-background) px-3 py-2 text-sm text-(--fm-text-primary)"
            placeholder={tt("Organisation", "المؤسسة")} />
        </label>
      </div>

      {channel === "email" ? (
        <label className="block space-y-1">
          <span className="text-xs font-medium text-(--fm-text-secondary)">{tt("Your email", "بريدك الإلكتروني")}</span>
          <input type="email" dir="ltr" value={email} onChange={(e) => setEmail(e.target.value)}
            toolparamdescription="Your email address so we can reply"
            className="w-full rounded-lg border border-(--fm-border) bg-(--fm-background) px-3 py-2 text-sm text-(--fm-text-primary)"
            placeholder="you@example.com" />
        </label>
      ) : (
        <>
          <label className="block space-y-1">
            <span className="text-xs font-medium text-(--fm-text-secondary)">
              {channel === "whatsapp" ? tt("Your WhatsApp number", "رقم الواتساب") : tt("Your phone number", "رقم هاتفك")}
            </span>
            <input type="tel" dir="ltr" value={phone} onChange={(e) => setPhone(e.target.value)}
              toolparamdescription="Your contact phone or WhatsApp number"
              className="w-full rounded-lg border border-(--fm-border) bg-(--fm-background) px-3 py-2 text-sm text-(--fm-text-primary)"
              placeholder="+974 5xxx xxxx" />
          </label>
          <label className="block space-y-1">
            <span className="text-xs font-medium text-(--fm-text-secondary)">{tt("Email for a receipt (optional)", "بريد لإيصال التأكيد (اختياري)")}</span>
            <input type="email" dir="ltr" value={email} onChange={(e) => setEmail(e.target.value)}
              toolparamdescription="Optional email address to send a receipt to"
              className="w-full rounded-lg border border-(--fm-border) bg-(--fm-background) px-3 py-2 text-sm text-(--fm-text-primary)"
              placeholder="you@example.com" />
          </label>
        </>
      )}

      <label className="block space-y-1">
        <span className="text-xs font-medium text-(--fm-text-secondary)">{tt("Your message (optional)", "رسالتك (اختياري)")}</span>
        <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)}
          toolparamdescription="The main text of your inquiry or question"
          className="w-full rounded-lg border border-(--fm-border) bg-(--fm-background) px-3 py-2 text-sm text-(--fm-text-primary)"
          placeholder={tt("What would you like to ask?", "ما الذي تودّ الاستفسار عنه؟")} />
      </label>

      {TURNSTILE_SITE_KEY && <div ref={tsRef} className="flex justify-center" />}
      {error && <p className="text-sm text-(--fm-danger)">{error}</p>}

      <div className="flex flex-wrap gap-2">
        <button type="submit" disabled={pending || captchaPending}
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-(--fm-primary) px-4 py-2 font-semibold text-[#0A1628] transition hover:opacity-90 disabled:opacity-60">
          {pending ? <Loader2 className="size-4 animate-spin" /> : null}
          {tt("Send", "إرسال")}
        </button>
        <a href={waHref} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#25D366] px-4 py-2 font-semibold text-[#0A1628]">
          <MessageCircle className="size-4" /> {tt("WhatsApp", "واتساب")}
        </a>
      </div>
    </form>
  );
}
